'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { ShoppingCart, Copy, Check, RotateCcw, ArrowRight, Settings2 } from 'lucide-react';
import Link from 'next/link';
import { logToolUsage } from '@/lib/tools/analytics';
import { ToolSignupWall } from './tool-signup-wall';
import {
  AMAZON_REFERRAL_CATEGORIES,
  AMAZON_FBA_SIZE_TIERS,
  AMAZON_MONTHLY_FEE,
  AMAZON_VAT_RATE,
  AMAZON_DATA_UPDATED,
} from '@/lib/tools/marketplace-data';

interface AmazonCalculatorProps {
  locale: Locale;
  content: ToolContent;
}

export function AmazonCalculator({ locale, content }: AmazonCalculatorProps) {
  const [fulfillment, setFulfillment] = useState<'fba' | 'fbm'>('fba');
  const [sizeTier, setSizeTier] = useState('small-parcel');
  const [referralCategory, setReferralCategory] = useState('home-garden');
  const [sellingPrice, setSellingPrice] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [productWeight, setProductWeight] = useState('');
  const [monthlyVolume, setMonthlyVolume] = useState('100');
  const [ownShippingCost, setOwnShippingCost] = useState('');
  const [includeVat, setIncludeVat] = useState(false);
  const [includeMonthlyFee, setIncludeMonthlyFee] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const hasLoggedRef = useRef(false);

  const labels = content.labels;

  useEffect(() => {
    const sell = parseFloat(sellingPrice.replace(',', '.'));
    if (!hasLoggedRef.current && !isNaN(sell) && sell > 0) {
      hasLoggedRef.current = true;
      logToolUsage({ tool_name: 'amazon-calculator', locale });
    }
  }, [sellingPrice, locale]);

  const selectedTier = AMAZON_FBA_SIZE_TIERS.find(t => t.id === sizeTier) ?? AMAZON_FBA_SIZE_TIERS[2];
  const selectedCategory = AMAZON_REFERRAL_CATEGORIES.find(c => c.id === referralCategory) ?? AMAZON_REFERRAL_CATEGORIES[0];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const calculateFbaFee = () => {
    const weight = parseFloat(productWeight.replace(',', '.')) || 0;
    let fee = selectedTier.baseFee;
    if (weight > selectedTier.perKgAbove && selectedTier.perKgRate > 0) {
      fee += (weight - selectedTier.perKgAbove) * selectedTier.perKgRate;
    }
    return fee;
  };

  const calculateResults = () => {
    const selling = parseFloat(sellingPrice.replace(',', '.')) || 0;
    const purchasing = parseFloat(purchasePrice.replace(',', '.')) || 0;
    const volume = parseInt(monthlyVolume) || 0;

    if (selling <= 0) return null;

    const applyVat = (amount: number) => includeVat ? amount * (1 + AMAZON_VAT_RATE) : amount;

    const referralFee = Math.max(selling * selectedCategory.rate, selectedCategory.minFee);
    const shippingFee = fulfillment === 'fba' ? calculateFbaFee() : (parseFloat(ownShippingCost.replace(',', '.')) || 0);
    const monthlyFeePerUnit = includeMonthlyFee && volume > 0 ? AMAZON_MONTHLY_FEE / volume : 0;
    const totalCostPerUnit = referralFee + shippingFee + monthlyFeePerUnit;
    const profitPerUnit = selling - purchasing - totalCostPerUnit;
    const margin = (profitPerUnit / selling) * 100;

    return {
      referralFee: applyVat(referralFee),
      referralRate: selectedCategory.rate,
      shippingFee: applyVat(shippingFee),
      monthlyFeePerUnit: applyVat(monthlyFeePerUnit),
      totalCostPerUnit: applyVat(totalCostPerUnit),
      profitPerUnit: applyVat(profitPerUnit),
      margin,
      monthlyProfit: applyVat(profitPerUnit) * volume,
      yearlyProfit: applyVat(profitPerUnit) * volume * 12,
      monthlyRevenue: applyVat(selling) * volume,
      monthlyCost: applyVat(totalCostPerUnit) * volume,
    };
  };

  const result = calculateResults();

  const reset = () => {
    setFulfillment('fba');
    setSizeTier('small-parcel');
    setReferralCategory('home-garden');
    setSellingPrice('');
    setPurchasePrice('');
    setProductWeight('');
    setMonthlyVolume('100');
    setOwnShippingCost('');
    setIncludeVat(false);
    setIncludeMonthlyFee(true);
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100">
          <ShoppingCart className="h-7 w-7 text-orange-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{content.title}</h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      {/* Calculator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Fulfillment Method */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.fulfillment || 'Fulfillment methode'}</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFulfillment('fba')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                fulfillment === 'fba' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              FBA (Fulfillment by Amazon)
            </button>
            <button
              type="button"
              onClick={() => setFulfillment('fbm')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                fulfillment === 'fbm' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              FBM (Eigen verzending)
            </button>
          </div>
        </div>

        {/* Selling Price */}
        <div className="mb-6">
          <label htmlFor="amazonSellingPrice" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.sellingPrice || 'Verkoopprijs'}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
            <input
              type="text"
              id="amazonSellingPrice"
              value={sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Purchase Price */}
        <div className="mb-6">
          <label htmlFor="amazonPurchasePrice" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.purchasePrice || 'Inkoopprijs'}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
            <input
              type="text"
              id="amazonPurchasePrice"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              placeholder="0,00"
              className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
        </div>

        {/* Referral Category */}
        <div className="mb-6">
          <label htmlFor="referralCategory" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.referralCategory || 'Productcategorie'}
          </label>
          <select
            id="referralCategory"
            value={referralCategory}
            onChange={(e) => setReferralCategory(e.target.value)}
            className="block w-full rounded-xl border border-slate-300 py-3 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            {AMAZON_REFERRAL_CATEGORIES.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label} ({(cat.rate * 100).toFixed(0)}%)
              </option>
            ))}
          </select>
        </div>

        {/* FBA Size Tier (only for FBA) */}
        {fulfillment === 'fba' && (
          <>
            <div className="mb-6">
              <label htmlFor="fbaSizeTier" className="mb-2 block text-sm font-medium text-slate-700">
                {labels.sizeTier || 'FBA pakketgrootte'}
              </label>
              <select
                id="fbaSizeTier"
                value={sizeTier}
                onChange={(e) => setSizeTier(e.target.value)}
                className="block w-full rounded-xl border border-slate-300 py-3 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                {AMAZON_FBA_SIZE_TIERS.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.label} — {tier.maxDimension} (€{tier.baseFee.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            {/* Product Weight */}
            <div className="mb-6">
              <label htmlFor="productWeight" className="mb-2 block text-sm font-medium text-slate-700">
                {labels.productWeight || 'Productgewicht (kg)'}
              </label>
              <input
                type="text"
                id="productWeight"
                value={productWeight}
                onChange={(e) => setProductWeight(e.target.value)}
                placeholder="0,5"
                className="block w-full rounded-xl border border-slate-300 py-3 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
          </>
        )}

        {/* FBM Shipping Cost */}
        {fulfillment === 'fbm' && (
          <div className="mb-6">
            <label htmlFor="fbmShippingCost" className="mb-2 block text-sm font-medium text-slate-700">
              {labels.ownShippingCost || 'Eigen verzendkosten'}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
              <input
                type="text"
                id="fbmShippingCost"
                value={ownShippingCost}
                onChange={(e) => setOwnShippingCost(e.target.value)}
                placeholder="0,00"
                className="block w-full rounded-xl border border-slate-300 py-3 pl-8 pr-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
          </div>
        )}

        {/* Monthly Volume */}
        <div className="mb-6">
          <label htmlFor="amazonMonthlyVolume" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.monthlyVolume || 'Maandelijks volume'}
          </label>
          <input
            type="number"
            id="amazonMonthlyVolume"
            value={monthlyVolume}
            onChange={(e) => setMonthlyVolume(e.target.value)}
            min="0"
            placeholder="100"
            className="block w-full rounded-xl border border-slate-300 py-3 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Toggles */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm font-medium text-slate-700">{labels.includeMonthlyFee || 'Maandelijks abonnement (€39) meenemen'}</span>
            <button
              type="button"
              onClick={() => setIncludeMonthlyFee(!includeMonthlyFee)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeMonthlyFee ? 'bg-brand-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeMonthlyFee ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
            <span className="text-sm font-medium text-slate-700">{labels.includeVat || 'Inclusief BTW (21%)'}</span>
            <button
              type="button"
              onClick={() => setIncludeVat(!includeVat)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeVat ? 'bg-brand-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeVat ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 space-y-3 rounded-xl bg-slate-50 p-4">
            <ResultRow label={`${labels.referralFee || 'Referral fee'} (${(result.referralRate * 100).toFixed(0)}%)`} value={formatCurrency(result.referralFee)} copyKey="referralFee" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <ResultRow label={fulfillment === 'fba' ? (labels.fbaFee || 'FBA fulfillment fee') : (labels.shippingCost || 'Verzendkosten')} value={formatCurrency(result.shippingFee)} copyKey="shippingFee" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            {result.monthlyFeePerUnit > 0 && (
              <ResultRow label={labels.monthlyFeePerUnit || 'Abonnement per eenheid'} value={formatCurrency(result.monthlyFeePerUnit)} copyKey="monthlyFee" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            )}
            <div className="border-t border-slate-200 pt-3">
              <ResultRow label={labels.totalCostPerUnit || 'Totale kosten per eenheid'} value={formatCurrency(result.totalCostPerUnit)} copyKey="totalCost" copied={copied} onCopy={copyToClipboard} bold copyLabel={labels.copyResult} />
            </div>
            <div className="border-t border-slate-200 pt-3">
              <ResultRow
                label={labels.profitPerUnit || 'Winst per eenheid'}
                value={formatCurrency(result.profitPerUnit)}
                copyKey="profit"
                copied={copied}
                onCopy={copyToClipboard}
                bold
                valueColor={result.profitPerUnit >= 0 ? 'text-green-600' : 'text-red-600'}
                copyLabel={labels.copyResult}
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">{labels.margin || 'Marge'}</span>
                <span className={`text-sm font-medium ${result.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {result.margin.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-2">
              <ResultRow label={labels.monthlyProfit || 'Maandelijkse winst'} value={formatCurrency(result.monthlyProfit)} copyKey="monthlyProfit" copied={copied} onCopy={copyToClipboard} valueColor={result.monthlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
              <ResultRow label={labels.yearlyProfit || 'Jaarlijkse winst'} value={formatCurrency(result.yearlyProfit)} copyKey="yearlyProfit" copied={copied} onCopy={copyToClipboard} bold valueColor={result.yearlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
            </div>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset || 'Reset'}
        </button>
      </div>

      {/* Data Updated Notice */}
      <div className="mt-3 text-center text-xs text-slate-400">
        {labels.dataUpdated || 'Tarieven bijgewerkt'}: {AMAZON_DATA_UPDATED}
      </div>

      {/* Fee Tables */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{labels.referralTable || 'Amazon referral fees per categorie'}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 text-left font-medium text-slate-600">{labels.category || 'Categorie'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.referralRate || 'Referral fee'}</th>
                <th className="py-2 pl-4 text-right font-medium text-slate-600">{labels.minFee || 'Min. fee'}</th>
              </tr>
            </thead>
            <tbody>
              {AMAZON_REFERRAL_CATEGORIES.map((cat) => (
                <tr key={cat.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 text-slate-700">{cat.label}</td>
                  <td className="py-2 px-4 text-right font-medium text-slate-900">{(cat.rate * 100).toFixed(0)}%</td>
                  <td className="py-2 pl-4 text-right text-slate-600">{formatCurrency(cat.minFee)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{labels.fbaTable || 'Amazon FBA fulfillment tarieven'}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 text-left font-medium text-slate-600">{labels.sizeTierLabel || 'Grootte'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.maxDimension || 'Max. afmeting'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.baseFee || 'Basistarief'}</th>
                <th className="py-2 pl-4 text-right font-medium text-slate-600">{labels.perKg || 'Per extra kg'}</th>
              </tr>
            </thead>
            <tbody>
              {AMAZON_FBA_SIZE_TIERS.map((tier) => (
                <tr key={tier.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 font-medium text-slate-700">{tier.label}</td>
                  <td className="py-2 px-4 text-right text-slate-600 text-xs">{tier.maxDimension}</td>
                  <td className="py-2 px-4 text-right text-slate-900">{formatCurrency(tier.baseFee)}</td>
                  <td className="py-2 pl-4 text-right text-slate-600">
                    {tier.perKgRate > 0 ? `${formatCurrency(tier.perKgRate)} (>${tier.perKgAbove}kg)` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      <ToolSignupWall locale={locale} />

      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">{index + 1}</span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{content.disclaimer.heading}</h3>
          <p className="text-xs text-slate-500 leading-relaxed">{content.disclaimer.content}</p>
        </div>
      )}
    </div>
  );
}

function ResultRow({ label, value, copyKey, copied, onCopy, bold, valueColor, copyLabel }: {
  label: string;
  value: string;
  copyKey: string;
  copied: string | null;
  onCopy: (value: string, key: string) => void;
  bold?: boolean;
  valueColor?: string;
  copyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-medium text-slate-700' : 'text-slate-600'}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`${bold ? 'text-lg font-semibold' : 'font-medium'} ${valueColor || 'text-slate-900'}`}>{value}</span>
        <button
          onClick={() => onCopy(value.replace(/[^0-9,.-]/g, ''), copyKey)}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          title={copyLabel || 'Copy'}
        >
          {copied === copyKey ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
